// TrainingLog Action Types
export const TrainingLogActionTypes = {
  // Create
  CREATE_START: 'TRAINING_LOG_CREATE_START',
  CREATE_SUCCESS: 'TRAINING_LOG_CREATE_SUCCESS',
  CREATE_ERROR: 'TRAINING_LOG_CREATE_ERROR',
  // Read (Latest)
  FETCH_LATEST_START: 'TRAINING_LOG_FETCH_LATEST_START',
  FETCH_LATEST_SUCCESS: 'TRAINING_LOG_FETCH_LATEST_SUCCESS',
  FETCH_LATEST_ERROR: 'TRAINING_LOG_FETCH_LATEST_ERROR',
  // Read (Summaries)
  FETCH_SUMMARIES_START: 'TRAINING_LOG_FETCH_SUMMARIES_START',
  FETCH_SUMMARIES_SUCCESS: 'TRAINING_LOG_FETCH_SUMMARIES_SUCCESS',
  FETCH_SUMMARIES_ERROR: 'TRAINING_LOG_FETCH_SUMMARIES_ERROR',
  // Update
  UPDATE_START: 'TRAINING_LOG_UPDATE_START',
  UPDATE_SUCCESS: 'TRAINING_LOG_UPDATE_SUCCESS',
  UPDATE_ERROR: 'TRAINING_LOG_UPDATE_ERROR',
  // Delete
  DELETE_START: 'TRAINING_LOG_DELETE_START',
  DELETE_SUCCESS: 'TRAINING_LOG_DELETE_SUCCESS',
  DELETE_ERROR: 'TRAINING_LOG_DELETE_ERROR',
  // Reset
  RESET: 'TRAINING_LOG_RESET',
  RESET_FLAGS: 'TRAINING_LOG_RESET_FLAGS',
} as const;

// Dominant Hand Type
export type DominantHand = 'Right' | 'Left';

// Training Type
export type TrainingType = 'adl' | 'locomo';

// TrainingLog Data Model
export interface TrainingLog {
  user_id: string;
  training_id: number;
  started_at: string; // yyyy-mm-dd HH:MM:SS
  ended_at?: string; // yyyy-mm-dd HH:MM:SS
  count?: number;
  average_angle?: number;
  average_duration?: number;
  initial_angle?: number;
  target_angle?: number;
  angles?: number[];
  absolute_angles?: number[];
  durations?: number[];
  dominant_hand?: DominantHand;
  with_band?: boolean;
  created_at?: string;
  updated_at?: string;
}

// Create Request Body
export interface TrainingLogCreateRequest {
  training_id: number;
  started_at: string; // yyyy-mm-dd HH:MM:SS (required)
  ended_at?: string;
  count?: number;
  average_angle?: number;
  average_duration?: number;
  initial_angle?: number;
  target_angle?: number;
  angles?: number[];
  absolute_angles?: number[];
  durations?: number[];
  dominant_hand?: DominantHand;
  with_band?: boolean;
}

// Update Request Body
export interface TrainingLogUpdateRequest {
  count?: number;
  average_angle?: number;
  average_duration?: number;
  ended_at?: string;
  with_band?: boolean;
  initial_angle?: number;
  target_angle?: number;
  angles?: number[];
  absolute_angles?: number[];
  durations?: number[];
  dominant_hand?: DominantHand;
}

// Summary by training_id
export interface TrainingLogSummary {
  user_id: string;
  count?: number;
  average_angle?: number;
  average_duration?: number;
}

// Summaries Response - date -> training_id -> summary
export type TrainingLogSummariesResponse = {
  [date: string]: {
    [trainingId: string]: TrainingLogSummary;
  };
};

// Redux State Type
export interface TrainingLogState {
  // Current data
  latest: TrainingLog | null;
  summaries: TrainingLogSummariesResponse | null;
  // Loading states
  loading: boolean;
  // Operation flags
  created: boolean;
  updated: boolean;
  deleted: boolean;
  // Error
  error: string | null;
}

// Action Types
export type TrainingLogAction =
  // Create
  | { type: typeof TrainingLogActionTypes.CREATE_START }
  | { type: typeof TrainingLogActionTypes.CREATE_SUCCESS; payload: TrainingLog }
  | { type: typeof TrainingLogActionTypes.CREATE_ERROR; error: string }
  // Read (Latest)
  | { type: typeof TrainingLogActionTypes.FETCH_LATEST_START }
  | { type: typeof TrainingLogActionTypes.FETCH_LATEST_SUCCESS; payload: TrainingLog }
  | { type: typeof TrainingLogActionTypes.FETCH_LATEST_ERROR; error: string }
  // Read (Summaries)
  | { type: typeof TrainingLogActionTypes.FETCH_SUMMARIES_START }
  | { type: typeof TrainingLogActionTypes.FETCH_SUMMARIES_SUCCESS; payload: TrainingLogSummariesResponse }
  | { type: typeof TrainingLogActionTypes.FETCH_SUMMARIES_ERROR; error: string }
  // Update
  | { type: typeof TrainingLogActionTypes.UPDATE_START }
  | { type: typeof TrainingLogActionTypes.UPDATE_SUCCESS }
  | { type: typeof TrainingLogActionTypes.UPDATE_ERROR; error: string }
  // Delete
  | { type: typeof TrainingLogActionTypes.DELETE_START }
  | { type: typeof TrainingLogActionTypes.DELETE_SUCCESS }
  | { type: typeof TrainingLogActionTypes.DELETE_ERROR; error: string }
  // Reset
  | { type: typeof TrainingLogActionTypes.RESET }
  | { type: typeof TrainingLogActionTypes.RESET_FLAGS };
